import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { getAllPublishedBlogs } from "../../API/API";
import SEO from "../../components/SEO";
import BlogCard from "./BlogCard";

const BlogContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(8),
  maxWidth: "1200px !important",
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(8),
  marginBottom: theme.spacing(6),
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    gap: theme.spacing(4),
  },
}));

const HeaderImage = styled(Box)(({ theme }) => ({
  flex: "0 0 45%",
  display: "flex",
  alignItems: "center",
  "& img": {
    width: "100%",
    height: "auto",
    objectFit: "contain",
  },
}));

const HeaderContent = styled(Box)(({ theme }) => ({
  flex: "0 0 55%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
}));

const BlogLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontFamily: "Inter, sans-serif",
  fontSize: "14px",
  fontWeight: 600,
  lineHeight: "20px",
  letterSpacing: "0",
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(1),
  "&::before": {
    content: '""',
    width: "24px",
    height: "2px",
    backgroundColor: theme.palette.primary.main,
    marginRight: theme.spacing(1),
    display: "inline-block",
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontFamily: "Inter, sans-serif",
  fontSize: "28px",
  fontWeight: 600,
  lineHeight: "36px",
  color: "#1A1A1A",
  marginBottom: theme.spacing(1),
}));

const Description = styled(Typography)(({ theme }) => ({
  fontFamily: "Inter, sans-serif",
  color: "#666666",
  fontSize: "16px",
  lineHeight: "24px",
  fontWeight: 400,
  maxWidth: "700px",
}));

const Blog = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await getAllPublishedBlogs();

        if (response && response.result && Array.isArray(response.result)) {
          const sortedPosts = response.result.sort((a, b) => {
            const firstPostSortOrder = parseInt(a.sortOrder) || 0;
            const secondPostSortOrder = parseInt(b.sortOrder) || 0;

            if (firstPostSortOrder !== secondPostSortOrder) {
              return firstPostSortOrder - secondPostSortOrder;
            }

            const firstPostCreationDate = new Date(a.createDatetime).getTime();
            const secondPostCreationDate = new Date(b.createDatetime).getTime();
            return firstPostCreationDate - secondPostCreationDate;
          });
          setBlogPosts(sortedPosts);
        } else {
          setBlogPosts([]);
        }
      } catch (error) {
        console.error("Error fetching blogs:", error);
        setBlogPosts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  return (
    <>
      <SEO
        title="Blog | Insights & Resources for Business Growth"
        description="Explore expert articles, tips, and updates on business finance,
                  funding options, credit improvement, and more."
        canonical="https://www.dequitec.com/blog"
      />
      <BlogContainer>
        <HeaderSection>
          <HeaderImage>
            <img src="/BlogHeader.png" alt="Blog Header Illustration" />
          </HeaderImage>
          <HeaderContent>
            <BlogLabel>Blog</BlogLabel>
            <Title variant="h1" component="h1">
              Insights & Resources for Business Growth
            </Title>
            <Description>
              Explore expert articles, tips, and updates on business finance,
              funding options, credit improvement, and more.
            </Description>
          </HeaderContent>
        </HeaderSection>

        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
          </Box>
        ) : blogPosts && blogPosts.length > 0 ? (
          <Grid container spacing={4}>
            {blogPosts.map((post) => (
              <Grid item xs={12} md={6} key={post.id}>
                <BlogCard post={post} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <Typography variant="h6" color="textSecondary">
              No blog posts available
            </Typography>
          </Box>
        )}
      </BlogContainer>
    </>
  );
};

export default Blog;
